<template>
  <v-dialog
    v-model="dialog"
    max-width="700"
    persistent
  >
    <v-card class="proof-dialog-card">
      <v-card-title class="text-h5 d-flex justify-space-between align-center proof-dialog-title">
        <span>Proof of Payment</span>
        <v-btn
          icon
          @click="closeDialog"
          size="small"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-0 proof-dialog-body">
        <!-- Loading State -->
        <div v-if="loading" class="text-center pa-8">
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          ></v-progress-circular>
          <p class="text-subtitle-1 mt-4">Loading proof of payment...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center pa-8">
          <v-icon size="80" color="error">mdi-alert-circle</v-icon>
          <h2 class="text-h6 mt-4 mb-2">Error Loading Proof</h2>
          <p class="text-body-1">{{ error }}</p>
        </div>

        <!-- Success State -->
        <div v-else class="pa-6">
          <v-card outlined class="receipt-card">
            <v-card-text class="receipt-card-text">
              <div class="receipt-header">
                <div class="brand-logo">
                  <v-icon size="32" color="primary">mdi-cash-multiple</v-icon>
                  <span class="brand-name">cash.co.zm</span>
                </div>
                <h3 class="receipt-title">Payment Proof</h3>
                <p class="receipt-subtitle">Official Transaction Receipt</p>
              </div>
              <div class="receipt-container">
                <pre class="receipt-text">{{ proofOfPayment }}</pre>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>

      <v-divider v-if="!loading && !error"></v-divider>

      <!-- Actions -->
      <v-card-actions v-if="!loading && !error" class="pa-4 proof-dialog-actions">
        <v-spacer></v-spacer>
        <v-btn
          color="secondary"
          variant="outlined"
          @click="copyToClipboard"
        >
          <v-icon left>mdi-content-copy</v-icon>
          Copy
        </v-btn>
        <v-btn
          color="primary"
          variant="outlined"
          @click="printReceipt"
        >
          <v-icon left>mdi-printer</v-icon>
          Print
        </v-btn>
        <v-btn
          color="primary"
          @click="closeDialog"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ProofOfPaymentDialog',
  props: {
    transactionId: {
      type: [Number, String],
      required: true
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: false,
      loading: false,
      error: null,
      proofOfPayment: ''
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        this.dialog = newVal;
        if (newVal && this.transactionId) {
          this.fetchProofOfPayment();
        }
      }
    },
    dialog(newVal) {
      if (!newVal) {
        this.$emit('close');
      }
    }
  },
  methods: {
    async fetchProofOfPayment() {
      try {
        this.loading = true;
        this.error = null;
        this.proofOfPayment = '';

        const response = await fetch(`${pettyCashApi}api/kazang/payment/transactions/${this.transactionId}/proof`);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Proof of payment not found for this transaction');
          } else {
            throw new Error(`Failed to load proof of payment (Status: ${response.status})`);
          }
        }

        this.proofOfPayment = await response.text();
        console.log('Proof of payment loaded:', this.proofOfPayment);

      } catch (error) {
        console.error('Error fetching proof of payment:', error);
        this.error = error.message || 'An error occurred while loading the proof of payment';
      } finally {
        this.loading = false;
      }
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.proofOfPayment);
        this.$emit('copied');
        // You could add a snackbar notification here
      } catch (error) {
        console.error('Failed to copy to clipboard:', error);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = this.proofOfPayment;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.$emit('copied');
        } catch (fallbackError) {
          console.error('Fallback copy failed:', fallbackError);
        }
        document.body.removeChild(textArea);
      }
    },
    printReceipt() {
      const printWindow = window.open('', '_blank');
      const doc = printWindow.document;
      doc.write(`<!DOCTYPE html><html><head><title>Proof of Payment - Transaction #${this.transactionId}</title><style>
        body{font-family:'Courier New',monospace;padding:20px;white-space:pre-wrap;line-height:1.4;color:#000;background:#fff}
        .receipt{background:white;padding:20px;max-width:600px;margin:0 auto}
        @media print{body{padding:0}}
      </style></head><body><div class="receipt" id="r"></div></body></html>`);
      doc.close();
      doc.getElementById('r').textContent = this.proofOfPayment;
      printWindow.focus();
      setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
    },
    closeDialog() {
      this.dialog = false;
    }
  }
};
</script>

<style scoped>
.proof-dialog-card {
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgb(var(--v-theme-border-color));
  box-shadow: 0 12px 32px rgba(var(--v-theme-on-surface-rgb), 0.15);
}

.proof-dialog-title {
  color: rgb(var(--v-theme-on-surface));
}

.proof-dialog-body {
  background: rgb(var(--v-theme-surface));
}

.receipt-card {
  background: #f5f5f5;
  border-color: #e0e0e0;
}

.receipt-card-text {
  color: rgb(var(--v-theme-on-surface));
}

.receipt-header {
  text-align: center;
  padding: 20px;
  background: linear-gradient(
    135deg,
    rgba(var(--v-theme-primary-rgb), 0.1),
    rgba(var(--v-theme-primary-rgb), 0.05)
  );
  border-radius: 8px;
  margin-bottom: 20px;
}

.brand-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 12px;
}

.brand-name {
  font-size: 24px;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
  letter-spacing: 1px;
}

.receipt-title {
  font-size: 20px;
  font-weight: 600;
  margin: 8px 0;
  color: rgb(var(--v-theme-on-surface));
}

.receipt-subtitle {
  font-size: 14px;
  color: rgb(var(--v-theme-on-surface-variant));
  margin: 0;
}

.receipt-container {
  background-color: #fafafa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow-x: auto;
  max-height: 60vh;
  overflow-y: auto;
}

.receipt-text {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  padding: 16px;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #1a1a1a;
  overflow-x: auto;
  text-align: left;
}

.proof-dialog-actions {
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgb(var(--v-theme-border-color));
}

@media print {
  .proof-dialog-card,
  .proof-dialog-title,
  .proof-dialog-actions {
    display: none !important;
  }

  .receipt-container {
    background: white !important;
    border: none !important;
    max-height: none !important;
    overflow: visible !important;
  }

  .receipt-text {
    color: #000 !important;
    padding: 0 !important;
  }
}
</style>
